import { useTranslation } from '@repo/i18n'
import type { Column } from '@tanstack/react-table'
import {
	ArrowDown,
	ArrowLeftToLine,
	ArrowRightToLine,
	ArrowUp,
	Check,
	EllipsisVertical,
	EyeOff,
	MoveHorizontal,
	Pin,
	PinOff,
	X
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Button } from '#ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger
} from '#ui/dropdown-menu'
import type { DataTableColumnMenuRender } from './types'

/**
 * The active sort direction is dropped, so the menu only offers the moves you
 * can still make (the other direction plus clear).
 */
function ColumnSortItems<TData>({
	column
}: {
	column: Column<TData, unknown>
}) {
	const { t } = useTranslation()
	const sorted = column.getIsSorted()
	return (
		<DropdownMenuGroup>
			{sorted !== 'asc' && (
				<DropdownMenuItem onClick={() => column.toggleSorting(false)}>
					<ArrowUp />
					{t('shared:data_table.column_menu.sort_asc')}
				</DropdownMenuItem>
			)}
			{sorted !== 'desc' && (
				<DropdownMenuItem onClick={() => column.toggleSorting(true)}>
					<ArrowDown />
					{t('shared:data_table.column_menu.sort_desc')}
				</DropdownMenuItem>
			)}
			{sorted !== false && (
				<DropdownMenuItem onClick={() => column.clearSorting()}>
					<X />
					{t('shared:data_table.column_menu.clear_sort')}
				</DropdownMenuItem>
			)}
		</DropdownMenuGroup>
	)
}

/**
 * Pin opens a submenu: pin left, pin right, or no pin, with the current state
 * ticked.
 */
function ColumnPinSubmenu<TData>({
	column
}: {
	column: Column<TData, unknown>
}) {
	const { t } = useTranslation()
	const pinned = column.getIsPinned()
	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger>
				<Pin />
				{t('shared:data_table.column_menu.pin_column')}
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent>
				<DropdownMenuItem
					data-active={pinned === 'left'}
					onClick={() => column.pin('left')}
				>
					<ArrowLeftToLine />
					{t('shared:data_table.column_menu.pin_left')}
					{pinned === 'left' && <Check className='ml-auto' />}
				</DropdownMenuItem>
				<DropdownMenuItem
					data-active={pinned === 'right'}
					onClick={() => column.pin('right')}
				>
					<ArrowRightToLine />
					{t('shared:data_table.column_menu.pin_right')}
					{pinned === 'right' && <Check className='ml-auto' />}
				</DropdownMenuItem>
				<DropdownMenuItem
					data-active={pinned === false}
					onClick={() => column.pin(false)}
				>
					<PinOff />
					{t('shared:data_table.column_menu.no_pin')}
					{pinned === false && <Check className='ml-auto' />}
				</DropdownMenuItem>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	)
}

/**
 * Per-column actions menu rendered in each header cell. Built-in actions map
 * directly to TanStack `Column` methods, so the menu stays in sync with table
 * state without local state. Visibility follows `enableColumnMenu` (column meta
 * overrides the table-level default). A `renderColumnMenuItems` renderer takes
 * full control of the item list and receives the built-in items to compose.
 */
export function DataTableColumnMenu<TData>(props: {
	column: Column<TData, unknown>
	label: string
	enableColumnMenu?: boolean
	onResetColumn?: (columnId: string) => void
	renderColumnMenuItems?: DataTableColumnMenuRender<TData>
}) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const meta = props.column.columnDef.meta
	const enabled = meta?.enableColumnMenu ?? props.enableColumnMenu ?? true
	const renderItems =
		meta?.renderColumnMenuItems ?? props.renderColumnMenuItems
	const onResetColumn = props.onResetColumn

	const canSort = props.column.getCanSort()
	const canPin = props.column.getCanPin()
	const canHide = props.column.getCanHide()
	const canAutoSize = props.column.getCanResize() && Boolean(onResetColumn)

	if (!enabled) {
		return null
	}
	if (!(canSort || canPin || canHide || canAutoSize || renderItems)) {
		return null
	}

	const sections: { key: string; node: ReactNode }[] = []
	if (canSort) {
		sections.push({
			key: 'sort',
			node: <ColumnSortItems column={props.column} key='sort' />
		})
	}
	if (canPin) {
		sections.push({
			key: 'pin',
			node: <ColumnPinSubmenu column={props.column} key='pin' />
		})
	}
	if (canAutoSize && onResetColumn) {
		sections.push({
			key: 'autosize',
			node: (
				<DropdownMenuItem
					key='autosize'
					onClick={() => {
						props.column.resetSize()
						onResetColumn(props.column.id)
					}}
				>
					<MoveHorizontal />
					{t('shared:data_table.column_menu.autosize_column')}
				</DropdownMenuItem>
			)
		})
	}
	if (canHide) {
		sections.push({
			key: 'hide',
			node: (
				<DropdownMenuItem
					key='hide'
					onClick={() => props.column.toggleVisibility(false)}
				>
					<EyeOff />
					{t('shared:data_table.column_menu.hide_column')}
				</DropdownMenuItem>
			)
		})
	}
	/** Flatten the present sections, inserting a separator only between them. */
	const internalItems = sections.flatMap((section, index) =>
		index === 0
			? [section.node]
			: [
					<DropdownMenuSeparator key={`sep-${section.key}`} />,
					section.node
				]
	)

	return (
		<DropdownMenu onOpenChange={(value) => setOpen(value)} open={open}>
			<DropdownMenuTrigger
				render={
					<Button
						aria-label={t('shared:data_table.column_menu.trigger', {
							column: props.label
						})}
						className='h-full shrink-0'
						size='icon-sm'
						type='button'
						variant='ghost'
					>
						<EllipsisVertical />
					</Button>
				}
			/>
			<DropdownMenuContent align='start' className='w-48'>
				{renderItems
					? renderItems({
							column: props.column,
							label: props.label,
							internalItems,
							closeMenu: () => setOpen(false)
						})
					: internalItems}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
